import React from "react";
import { Link } from "react-router-dom";

import { useSiteContent } from "../content/SiteContentContext.jsx";
import { resolveImage } from "../utils/resolveImage";
import { safeUrl } from "../utils/safeUrl";
import { RichText } from "../utils/richText.jsx";
import robot from "../assets/images/careers/robot.png";
import sep from "../assets/images/icons/sep.svg";
import rvoe from "../assets/images/icons/rvoe.svg";

const Bachillerato = () => {
  const { bachFeatures, bachillerato } = useSiteContent();
  const features = Array.isArray(bachFeatures) ? bachFeatures : [];

  return (
    <div className="w-full overflow-hidden">
      {/* ===== Hero ===== */}
      <section className="brand-soft relative overflow-hidden">
        <div className="mx-auto w-full max-w-[1180px] px-6 py-14 md:py-20">
          <div className="flex md:flex-row flex-col items-center gap-10">
            <div className="flex-1 fade-up">
              <span className="eyebrow">Bachillerato en línea</span>
              <h1 className="mt-4 font-bold text-tPrimary text-[40px] ss:text-[56px] leading-[1.05]">
                {bachillerato?.title}{" "}
                <span className="text-gradient">{bachillerato?.titleHighlight}</span>{" "}
                {bachillerato?.titleTail}
              </h1>

              <RichText
                html={bachillerato?.intro}
                className="mt-5 text-tPrimary text-[18px] leading-[30px] max-w-[520px]"
              />

              <ul className="brand-list mt-6 max-w-[520px] text-tPrimary text-[17px]">
                {(bachillerato?.bullets || []).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>

              <div className="flex flex-wrap gap-3 mt-8">
                <Link to="/contacto">
                  <button className="btn-brand">
                    Solicitar informes <i className="fa-solid fa-arrow-right" />
                  </button>
                </Link>
                <a href="#validez">
                  <button className="btn-ghost">
                    <i className="fa-solid fa-shield-halved" /> Validez oficial
                  </button>
                </a>
              </div>
            </div>

            <div className="flex-1 flex justify-center items-center relative w-full">
              <div className="blob-glow w-[380px] h-[380px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
              <img
                src={robot}
                alt="Bachillerato INCAP"
                className="w-[86%] max-w-[440px] relative z-[1] drop-shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===== Banda de sellos ===== */}
      <section className="brand-gradient text-white">
        <div className="mx-auto w-full max-w-[1180px] px-6 py-7 flex flex-wrap justify-center gap-3">
          <span className="pill pill-glass">
            <i className="fa-solid fa-house-laptop" /> Presencial o en línea
          </span>
          <span className="pill pill-glass">
            <i className="fa-solid fa-certificate" /> Validez oficial SEP
          </span>
          <span className="pill pill-glass">
            <i className="fa-solid fa-award" /> RVOE vigente
          </span>
          <span className="pill pill-glass">
            <i className="fa-solid fa-user-graduate" /> Acompañamiento cercano
          </span>
        </div>
      </section>

      {/* ===== Video ===== */}
      {bachillerato?.videoUrl ? (
        <section className="brand-soft py-14 px-6">
          <div className="video-frame">
            <iframe
              src={safeUrl(bachillerato.videoUrl)}
              title="Video del Bachillerato INCAP"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </section>
      ) : null}

      {/* ===== Objetivo del nivel ===== */}
      {bachillerato?.objective ? (
        <section className="py-16 px-6">
          <div className="mx-auto w-full max-w-[880px]">
            <div className="content-card fade-up">
              <div className="section-title mb-4">
                <span className="st-ico">
                  <i className="fa-solid fa-bullseye" />
                </span>
                Objetivo del nivel
              </div>
              <RichText html={bachillerato.objective} className="text-tPrimary leading-[28px]" />
            </div>
          </div>
        </section>
      ) : null}

      {/* ===== Características ===== */}
      <section className="brand-soft py-16 px-6">
        <div className="mx-auto w-full max-w-[1100px]">
          <div className="text-center max-w-[720px] mx-auto">
            <span className="eyebrow justify-center">¿Por qué INCAP?</span>
            <h2 className="mt-3 font-bold text-tPrimary text-[32px] ss:text-[40px] leading-tight">
              {bachillerato?.featuresTitle}
            </h2>
            {(bachillerato?.featuresText || []).map((paragraph, index) => (
              <p
                key={index}
                className="mt-4 text-tPrimary text-[18px] leading-[30px]"
              >
                {paragraph}
              </p>
            ))}
          </div>

          <div className="grid grid-cols-1 ss:grid-cols-2 gap-6 mt-12">
            {features.map((feature, index) => (
              <div
                key={index}
                className="content-card fade-up flex items-start gap-5 hover:-translate-y-1 transition-transform duration-300"
                style={{ animationDelay: `${index * 90}ms` }}
              >
                <div className="icon-orb w-[58px] h-[58px] flex-shrink-0">
                  <img
                    src={resolveImage(feature.icon)}
                    alt=""
                    className="w-[52%] h-[52%] object-contain brightness-0 invert"
                  />
                </div>
                <div>
                  <h4 className="font-semibold italic text-tPrimary text-[20px] mb-1.5">
                    {feature.title}
                  </h4>
                  <p className="text-tPrimary text-[16.5px] leading-[25px]">
                    {feature.content}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Perfil de ingreso / egreso ===== */}
      {(bachillerato?.admissionProfile || bachillerato?.graduationProfile) ? (
        <section className="py-16 px-6">
          <div className="mx-auto w-full max-w-[1100px] grid grid-cols-1 lg:grid-cols-2 gap-6">
            {bachillerato?.admissionProfile ? (
              <div className="content-card fade-up">
                <div className="section-title mb-4">
                  <span className="st-ico">
                    <i className="fa-solid fa-user-check" />
                  </span>
                  Perfil de ingreso
                </div>
                <RichText html={bachillerato.admissionProfile} className="text-tPrimary leading-[28px]" />
              </div>
            ) : null}
            {bachillerato?.graduationProfile ? (
              <div className="content-card fade-up" style={{ animationDelay: "110ms" }}>
                <div className="section-title mb-4">
                  <span className="st-ico">
                    <i className="fa-solid fa-user-graduate" />
                  </span>
                  Perfil de egreso
                </div>
                <RichText html={bachillerato.graduationProfile} className="text-tPrimary leading-[28px]" />
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      {/* ===== Validez oficial ===== */}
      <section id="validez" className="brand-soft py-16 px-6">
        <div className="mx-auto w-full max-w-[960px]">
          <div className="content-card flex flex-col sm:flex-row sm:items-center gap-8">
            <div className="flex items-center justify-center gap-6 sm:border-r sm:border-[#e4e9f7] sm:pr-8">
              <img src={sep} alt="SEP" className="w-[104px] object-contain" />
              <img src={rvoe} alt="RVOE" className="w-[104px] object-contain" />
            </div>
            <div className="flex-1">
              <div className="section-title mb-4">
                <span className="st-ico">
                  <i className="fa-solid fa-award" />
                </span>
                {bachillerato?.validityTitle}
              </div>
              <div className="flex flex-wrap gap-x-10 gap-y-2 text-tPrimary text-[17px]">
                <p>
                  <span className="font-semibold text-primary">Clave SEP:</span>{" "}
                  {bachillerato?.sepClave}
                </p>
                <p className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-primary">RVOE:</span>{" "}
                  {bachillerato?.rvoe}
                  {bachillerato?.rvoeLink ? (
                    <a
                      href={safeUrl(bachillerato.rvoeLink)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="button-red"
                    >
                      Consulta aquí
                    </a>
                  ) : null}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Bachillerato;
